import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { renderToStaticMarkup } from 'react-dom/server';
import 'react-quill/dist/quill.snow.css';
import { SmilePlus } from 'lucide-react';
import ColorSwatchPicker from './ColorSwatchPicker';
import {
  ALL_ICON_NAMES,
  PRIORITIZED_ICON_NAMES,
  getIconComponent,
} from './editorIcons';

const MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
    ['clean'],
  ],
};

const FORMATS = [
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'align', 'image',
];

const ICON_SIZE = 24;

export type IconStyleType = 'flat' | '3d';

const ICON_3D_DEFS = `<defs>
<linearGradient id="icon3dgrad" x1="0%" y1="0%" x2="100%" y2="100%">
  <stop offset="0%" style="stop-color:#818cf8"/>
  <stop offset="50%" style="stop-color:#6366f1"/>
  <stop offset="100%" style="stop-color:#4f46e5"/>
</linearGradient>
<filter id="icon3dfilter" x="-20%" y="-20%" width="140%" height="140%">
  <feDropShadow dx="0" dy="1" stdDeviation="0.5" flood-color="rgba(99,102,241,0.4)"/>
  <feDropShadow dx="0" dy="2" stdDeviation="1" flood-color="rgba(0,0,0,0.25)"/>
  <feDropShadow dx="0" dy="3" stdDeviation="1.5" flood-color="rgba(0,0,0,0.15)"/>
</filter>
</defs>`;

function iconToDataUrl(name: string, style: IconStyleType): string | null {
  const Icon = getIconComponent(name);
  if (!Icon) return null;
  try {
    const svg = renderToStaticMarkup(
      React.createElement(Icon, { size: ICON_SIZE })
    );
    let withXmlns = svg.includes('xmlns=') ? svg : svg.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
    if (style === '3d') {
      withXmlns = withXmlns.replace('<svg', '<svg filter="url(#icon3dfilter)"');
      withXmlns = withXmlns.replace(/<svg([^>]*)>/, `<svg$1>${ICON_3D_DEFS}`);
      withXmlns = withXmlns.replace(/\bstroke="[^"]*"/g, 'stroke="url(#icon3dgrad)"');
    }
    const encoded = encodeURIComponent(withXmlns);
    return `data:image/svg+xml,${encoded}`;
  } catch {
    return null;
  }
}

type RichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  textColor?: string;
  onTextColorChange?: (color: string) => void;
  backgroundColor?: string;
  onBackgroundColorChange?: (color: string) => void;
  onFocus?: () => void;
};

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  className,
  textColor,
  onTextColorChange,
  backgroundColor,
  onBackgroundColorChange,
  onFocus,
}: RichTextEditorProps) {
  const { t } = useTranslation();
  const quillRef = useRef<ReactQuill | null>(null);
  const iconPickerAnchorRef = useRef<HTMLButtonElement>(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [iconSearch, setIconSearch] = useState('');
  const [iconPickerPosition, setIconPickerPosition] = useState<{ top: number; left: number } | null>(null);
  const [iconStyle, setIconStyle] = useState<IconStyleType>('flat');

  useEffect(() => {
    if (!iconPickerOpen || !iconPickerAnchorRef.current) {
      setIconPickerPosition(null);
      return;
    }
    const rect = iconPickerAnchorRef.current.getBoundingClientRect();
    setIconPickerPosition({ left: rect.left, top: rect.bottom + 4 });
  }, [iconPickerOpen]);

  const showColorToolbar =
    (textColor !== undefined && onTextColorChange) ||
    (backgroundColor !== undefined && onBackgroundColorChange);

  const insertIcon = useCallback((name: string) => {
    const dataUrl = iconToDataUrl(name, iconStyle);
    if (!dataUrl) return;
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true);
    if (!range) return;
    editor.insertEmbed(range.index, 'image', dataUrl);
    setTimeout(() => {
      try {
        const leaf = editor.getLeaf(range.index);
        if (leaf && leaf[0] && (leaf[0] as { domNode?: HTMLElement }).domNode) {
          (leaf[0] as { domNode: HTMLElement }).domNode.classList.add('ql-inline-icon');
        }
      } catch {
        // ignore
      }
    }, 0);
    editor.setSelection({ index: range.index + 1, length: 0 });
    setIconPickerOpen(false);
    setIconSearch('');
  }, [iconStyle]);

  const filteredIconNames = iconSearch.trim()
    ? ALL_ICON_NAMES.filter((n) =>
        n.toLowerCase().includes(iconSearch.trim().toLowerCase())
      ).slice(0, 200)
    : [];

  const prioritizedFiltered = iconSearch.trim()
    ? PRIORITIZED_ICON_NAMES.filter((n) =>
        n.toLowerCase().includes(iconSearch.trim().toLowerCase())
      )
    : PRIORITIZED_ICON_NAMES;

  return (
    <div className={className} onFocusCapture={onFocus}>
      <div className="flex flex-col gap-0 [&_.ql-container]:rounded-b-none">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          modules={MODULES}
          formats={FORMATS}
          className="rich-text-editor-quill"
        />
        <div className="flex flex-wrap items-center gap-3 py-1.5 px-2 border border-t-0 border-gray-300 dark:border-gray-600 rounded-b-lg bg-gray-50 dark:bg-gray-800/50">
          <div className="relative">
            <button
              ref={iconPickerAnchorRef}
              type="button"
              onClick={() => setIconPickerOpen((o) => !o)}
              className="flex items-center gap-1.5 h-7 min-w-[2rem] px-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300 text-xs font-medium"
              title={t('richTextEditor.addIcon')}
            >
              <SmilePlus className="h-4 w-4" />
              {t('richTextEditor.icon')}
            </button>
            {iconPickerOpen && iconPickerPosition && typeof document !== 'undefined' && createPortal(
              <>
                <div
                  className="fixed inset-0 z-[9998]"
                  aria-hidden
                  onClick={() => setIconPickerOpen(false)}
                />
                <div
                  className="fixed z-[9999] w-[320px] max-h-[360px] flex flex-col rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl overflow-hidden"
                  style={{ left: iconPickerPosition.left, top: iconPickerPosition.top, minHeight: 280 }}
                >
                  <div className="p-2 border-b border-gray-200 dark:border-gray-600 flex-shrink-0 space-y-2">
                    <input
                      type="text"
                      value={iconSearch}
                      onChange={(e) => setIconSearch(e.target.value)}
                      placeholder={t('richTextEditor.searchIcon')}
                      className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm placeholder:text-gray-400"
                    />
                    <div className="flex flex-wrap gap-2">
                      <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide self-center">{t('richTextEditor.style')}</span>
                      <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                        {(['flat', '3d'] as const).map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setIconStyle(s)}
                            className={clsx(
                              'px-2.5 py-1 text-xs font-medium',
                              iconStyle === s
                                ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                            )}
                          >
                            {s === 'flat' ? t('richTextEditor.flat') : t('richTextEditor.threeD')}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-3">
                    {prioritizedFiltered.length > 0 && (
                      <div>
                        <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                          {t('richTextEditor.prioritized')}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {prioritizedFiltered.map((name) => {
                            const Icon = getIconComponent(name);
                            if (!Icon) return null;
                            return (
                              <button
                                key={name}
                                type="button"
                                onClick={() => insertIcon(name)}
                                className="p-1.5 rounded-lg border border-transparent hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                title={name}
                              >
                                <Icon size={20} className="shrink-0" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                        {iconSearch.trim() ? t('richTextEditor.results') : t('richTextEditor.allIcons')}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {(iconSearch.trim() ? filteredIconNames : ALL_ICON_NAMES.slice(0, 120)).map((name) => {
                          const Icon = getIconComponent(name);
                          if (!Icon) return null;
                          return (
                            <button
                              key={name}
                              type="button"
                              onClick={() => insertIcon(name)}
                              className="p-1.5 rounded-lg border border-transparent hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                              title={name}
                            >
                              <Icon size={18} className="shrink-0" />
                            </button>
                          );
                        })}
                      </div>
                      {iconSearch.trim() && filteredIconNames.length === 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
                          {t('richTextEditor.noMatchingIcon')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>,
              document.body
            )}
          </div>
          {showColorToolbar && (
            <>
              {textColor !== undefined && onTextColorChange && (
                <ColorSwatchPicker
                  label={t('richTextEditor.text')}
                  value={textColor}
                  onChange={onTextColorChange}
                  allowOpacity
                  className="flex items-center gap-1.5 [&_button]:!h-7 [&_button]:!min-w-7 [&_label]:!mb-0"
                />
              )}
              {backgroundColor !== undefined && onBackgroundColorChange && (
                <ColorSwatchPicker
                  label={t('richTextEditor.background')}
                  value={backgroundColor === 'transparent' ? 'transparent' : backgroundColor}
                  onChange={onBackgroundColorChange}
                  allowTransparent
                  allowOpacity
                  className="flex items-center gap-1.5 [&_button]:!h-7 [&_button]:!min-w-7 [&_label]:!mb-0"
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
